import React, { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Building2, Mail, Lock, User, Briefcase, ArrowRight, Check } from 'lucide-react'
import { KaryvaLogoAnimated3D } from '../components/KaryvaLogoAnimated3D'
import { useAuth } from '../contexts/AuthContext'
import { billingService } from '../services/billing'
import { labelsService } from '../services/labels'
import { toast } from 'sonner'
import { setToken } from '../utils/token'
import type { ApiErrorLike } from '../utils/apiError'

// ── Background decoration (matches LoginPage) ─────────────────────────────────
function BackgroundBlurs() {
  return (
    <div className="absolute inset-0 opacity-20 overflow-hidden pointer-events-none">
      <div className="absolute bg-[#486581] blur-3xl filter right-[10%] rounded-full w-[384px] h-[384px] top-[10%] opacity-30" />
      <div className="absolute bg-[#DAA520] blur-3xl filter left-[5%] rounded-full w-[384px] h-[384px] top-[40%] opacity-30" />
    </div>
  )
}

// ── Industry option ───────────────────────────────────────────────────────────
const INDUSTRY_ICONS: Record<string, string> = {
  construction: '🏗️',
  legal:        '⚖️',
  healthcare:   '🏥',
  marketing:    '📢',
  nonprofit:    '🏛️',
  finance:      '💼',
  defense:      '🛡️',
  generic:      '🏢',
}

interface IndustryOpt { key: string; name: string; description: string }

// ── Main component ────────────────────────────────────────────────────────────
export function SignupPage() {
  const navigate = useNavigate()
  // Sprint 30 — was `{ checkAuth } = useAuth() as any`: checkAuth is not
  // exposed by AuthContext, so it was always undefined and the context
  // never learned about the new session — ProtectedRoute then bounced
  // fresh signups to /login despite the valid token.
  const { refreshUser } = useAuth()

  const [step, setStep] = useState<1 | 2>(1)
  const [loading, setLoading] = useState(false)
  const [industries, setIndustries] = useState<IndustryOpt[]>([])

  // Form state
  const [fullName, setFullName]   = useState('')
  const [email, setEmail]         = useState('')
  const [password, setPassword]   = useState('')
  const [orgName, setOrgName]     = useState('')
  const [industry, setIndustry]   = useState('generic')

  useEffect(() => {
    // Sprint 16: was silent — non-critical (dropdown can be skipped or
    // filled in later via Settings), but log so dev tools surface it.
    labelsService.getIndustries().then(setIndustries).catch((err) => {
      console.warn('SignupPage getIndustries failed; dropdown will be empty:', err)
    })
  }, [])

  const handleStep1 = (e: React.FormEvent) => {
    e.preventDefault()
    if (!fullName.trim() || !email.trim() || !password.trim()) return
    setStep(2)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!orgName.trim()) return
    setLoading(true)
    try {
      const tokenData = await billingService.signup({
        full_name: fullName,
        email,
        password,
        org_name: orgName,
        industry: industry !== 'generic' ? industry : undefined,
      })
      // Store token and reload auth context
      setToken(tokenData.access_token)
      await refreshUser()
      toast.success(`Welcome to Karyva, ${fullName.split(' ')[0]}! Your 14-day trial starts now.`)
      navigate('/')
    } catch (errRaw) {
      const err = errRaw as ApiErrorLike
      const msg = err.response?.data?.detail
      toast.error(typeof msg === 'string' ? msg : 'Signup failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-[#0a0f1e] flex relative overflow-hidden">
      <BackgroundBlurs />

      {/* ── Left panel — marketing copy ──────────────────────────────── */}
      <div className="hidden lg:flex flex-col justify-between w-[420px] shrink-0 p-10 relative z-10">
        <KaryvaLogoAnimated3D />

        <div className="space-y-6">
          <h1 className="text-3xl font-bold text-white leading-tight">
            Project management<br />
            <span className="text-[#DAA520]">built for your industry</span>
          </h1>
          <p className="text-[#90a1b9] text-sm leading-relaxed">
            Construction punch lists. Legal matter tracking. Healthcare compliance.
            Karyva adapts to how your team actually works — not the other way around.
          </p>
          <div className="space-y-3">
            {[
              'Industry-specific templates in 60 seconds',
              'Photo evidence, sign-offs & audit trails',
              'Guest collaborators at no extra cost',
              'Enterprise-grade security from day one',
            ].map(text => (
              <div key={text} className="flex items-center gap-3 text-sm text-[#90a1b9]">
                <div className="w-5 h-5 rounded-full bg-[#DAA520]/20 flex items-center justify-center shrink-0">
                  <Check size={11} className="text-[#DAA520]" />
                </div>
                {text}
              </div>
            ))}
          </div>
        </div>

        <p className="text-[#90a1b9] text-xs">
          14-day free trial · No credit card required
        </p>
      </div>

      {/* ── Right panel — form ────────────────────────────────────────── */}
      <div className="flex-1 flex items-center justify-center p-6 relative z-10">
        <div className="w-full max-w-md">
          {/* Progress dots */}
          <div className="flex items-center gap-2 mb-6 justify-center">
            <div className={`w-2 h-2 rounded-full transition-colors ${step >= 1 ? 'bg-[#DAA520]' : 'bg-white/20'}`} />
            <div className={`w-8 h-0.5 transition-colors ${step >= 2 ? 'bg-[#DAA520]' : 'bg-white/20'}`} />
            <div className={`w-2 h-2 rounded-full transition-colors ${step >= 2 ? 'bg-[#DAA520]' : 'bg-white/20'}`} />
          </div>

          <div className="bg-white/[0.06] backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-2xl">

            {/* Step 1 — Account details */}
            {step === 1 && (
              <form onSubmit={handleStep1} className="space-y-5">
                <div>
                  <h2 className="text-xl font-bold text-white">Create your account</h2>
                  <p className="text-[#90a1b9] text-sm mt-1">Free for 14 days · No credit card needed</p>
                </div>

                <Field label="Full name" icon={<User size={15} />}>
                  <input
                    type="text"
                    value={fullName}
                    onChange={e => setFullName(e.target.value)}
                    placeholder="Jane Smith"
                    required
                    autoFocus
                    className={INPUT_CLS}
                  />
                </Field>

                <Field label="Work email" icon={<Mail size={15} />}>
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="jane@acmecorp.com"
                    required
                    className={INPUT_CLS}
                  />
                </Field>

                <Field label="Password" icon={<Lock size={15} />}>
                  <input
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    required
                    minLength={8}
                    className={INPUT_CLS}
                  />
                </Field>

                <button
                  type="submit"
                  disabled={!fullName || !email || password.length < 8}
                  className={BTN_CLS}
                >
                  Continue <ArrowRight size={16} />
                </button>

                {/* Marketing legal pages are served from www.karyva.com
                    (see LoginPage). Keep these URLs in sync if they move. */}
                <p className="text-center text-[#62748e] text-xs leading-relaxed">
                  By signing up, you agree to our{' '}
                  <a
                    href="https://www.karyva.com/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#DAA520] hover:underline"
                  >
                    Terms of Service
                  </a>{' '}
                  and{' '}
                  <a
                    href="https://www.karyva.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#DAA520] hover:underline"
                  >
                    Privacy Policy
                  </a>.
                </p>

                <p className="text-center text-[#90a1b9] text-sm">
                  Already have an account?{' '}
                  <Link to="/login" className="text-[#DAA520] hover:underline font-medium">Sign in</Link>
                </p>
              </form>
            )}

            {/* Step 2 — Organisation + industry */}
            {step === 2 && (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <h2 className="text-xl font-bold text-white">Set up your workspace</h2>
                  <p className="text-[#90a1b9] text-sm mt-1">We'll configure it for your industry instantly</p>
                </div>

                <Field label="Organisation name" icon={<Building2 size={15} />}>
                  <input
                    type="text"
                    value={orgName}
                    onChange={e => setOrgName(e.target.value)}
                    placeholder="Acme Corp"
                    required
                    autoFocus
                    className={INPUT_CLS}
                  />
                </Field>

                <div>
                  <label className="block text-xs font-semibold text-[#90a1b9] uppercase tracking-wide mb-2 flex items-center gap-2">
                    <Briefcase size={13} /> Industry
                  </label>
                  <div className="grid grid-cols-2 gap-2 max-h-52 overflow-y-auto pr-1">
                    {industries.map(opt => (
                      <label
                        key={opt.key}
                        className={`flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer transition-all text-sm
                          ${industry === opt.key
                            ? 'border-[#DAA520] bg-[#DAA520]/10 text-white'
                            : 'border-white/10 text-[#90a1b9] hover:border-white/30 hover:text-white'}`}
                      >
                        <input
                          type="radio"
                          name="industry"
                          value={opt.key}
                          checked={industry === opt.key}
                          onChange={() => setIndustry(opt.key)}
                          className="sr-only"
                        />
                        <span>{INDUSTRY_ICONS[opt.key] ?? '🏢'}</span>
                        <span className="font-medium">{opt.name}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="flex gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="flex-none px-4 py-2.5 rounded-xl border border-white/20 text-white/70
                               hover:border-white/40 hover:text-white transition-colors text-sm font-medium"
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={!orgName.trim() || loading}
                    className={`flex-1 ${BTN_CLS}`}
                  >
                    {loading ? 'Creating workspace…' : <>Start free trial <ArrowRight size={16} /></>}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Small helpers ─────────────────────────────────────────────────────────────

const INPUT_CLS = `w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5
  text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#DAA520]/60
  focus:ring-1 focus:ring-[#DAA520]/30 transition-colors`

const BTN_CLS = `w-full flex items-center justify-center gap-2 py-2.5 rounded-xl
  bg-[#DAA520] hover:bg-[#c4941b] disabled:opacity-50 disabled:cursor-not-allowed
  text-black font-semibold text-sm transition-colors`

function Field({ label, icon, children }: { label: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-[#90a1b9] uppercase tracking-wide mb-1.5 flex items-center gap-2">
        {icon} {label}
      </label>
      {children}
    </div>
  )
}
